'use client'

import {
  Hovercard,
  HovercardAnchor,
  HovercardProvider,
  useHovercardContext,
  useStoreState,
} from '@ariakit/react'
import { ComponentProps, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { NextLink } from '../navigation/NextLink'

type PopoverHovercardProviderProps = Omit<
  NonNullable<ComponentProps<typeof HovercardProvider>>,
  'timeout'
>

export const PopoverHovercardProvider = ({
  showTimeout = 0,
  hideTimeout = 0,
  ...props
}: PopoverHovercardProviderProps) => {
  return <HovercardProvider showTimeout={showTimeout} hideTimeout={hideTimeout} {...props} />
}

type PopoverHovercardTriggerProps = ComponentProps<typeof HovercardAnchor> & {
  openOnFocusVisible?: boolean
}

export const PopoverHovercardTrigger = ({
  render: renderProp,
  onFocusVisible: onFocusVisibleProp,
  href: hrefProp,
  openOnFocusVisible = true,
  ...props
}: PopoverHovercardTriggerProps) => {
  const hovercardContext = useHovercardContext()
  const onFocusVisible = useCallback<
    NonNullable<ComponentProps<typeof HovercardAnchor>['onFocusVisible']>
  >(
    arg1 => {
      onFocusVisibleProp?.(arg1)
      if (openOnFocusVisible) hovercardContext?.setOpen(true)
    },
    [hovercardContext, onFocusVisibleProp, openOnFocusVisible]
  )
  const render = useMemo(() => {
    return renderProp ?? (hrefProp ? <NextLink href={hrefProp} /> : <span />)
  }, [hrefProp, renderProp])
  return (
    <HovercardAnchor onFocusVisible={onFocusVisible} render={render} href={hrefProp} {...props} />
  )
}

const HiddenDisclosureButtonEl = styled.button`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  pointer-events: none;
`

export const PopoverHovercardDisclosureButton = ({
  'aria-label': ariaLabel,
}: {
  'aria-label': string
}) => {
  const hovercardContext = useHovercardContext()
  const isOpen = useStoreState(hovercardContext)?.open ?? false
  return (
    <HiddenDisclosureButtonEl
      type='button'
      aria-expanded={isOpen}
      aria-haspopup='true'
      aria-label={ariaLabel}
      onClick={() => hovercardContext?.setOpen(!isOpen)}
    />
  )
}

const hovercardAnimationDuration = 75

const StyledHovercard = styled(Hovercard).attrs(props => ({
  fixed: props.fixed ?? true,
}))`
  width: 300px;

  border-radius: ${o => o.theme.spacing(2)};
  padding: ${o => o.theme.spacing(2)};
  border-width: 1px;
  border-color: ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  background-color: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary.on.background};

  position: relative;
  z-index: ${o => o.theme.zIndex.popovers - 1};
  display: flex;
  border-style: solid;

  max-height: 70vh;
  max-height: 75svh;
  overflow: auto;

  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);

  transform-origin: top;

  transition:
    opacity ${hovercardAnimationDuration}ms ease,
    transform ${hovercardAnimationDuration}ms cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.7;
  &[data-enter='true'] {
    z-index: ${o => o.theme.zIndex.popovers};
    opacity: 1;
  }
`

type PopoverHovercardProps = ComponentProps<typeof Hovercard>

export const PopoverHovercard = ({
  gutter = 16,
  overflowPadding = 8,
  fixed = true,
  ...props
}: PopoverHovercardProps) => {
  return (
    <StyledHovercard gutter={gutter} overflowPadding={overflowPadding} fixed={fixed} {...props} />
  )
}

export const PopoverHovercardStyles = {
  Hovercard: StyledHovercard,
  hovercardAnimationDuration,
}
